import React, { useEffect, useState, useCallback } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Spin, 
  message, 
  Typography, 
  Modal, 
  DatePicker, 
  Table, 
  Statistic, 
  Space,
  Switch,
  InputNumber,
  Grid
} from 'antd';
import { FilterOutlined, SettingOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { reportService, StatsCardDto, HospitalOccupancyDto, VisitStatsDto, FinancialReportDto, DepartmentRevenueDto, ParaclinicalServiceStatsDto } from '../services/reportService';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  LabelList, 
  ComposedChart, 
  Bar, 
  Line,
  LineChart,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import './ReportTab.css';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const ReportTab: React.FC = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [stats, setStats] = useState<StatsCardDto[]>([]);
  const [hospitalOccupancy, setHospitalOccupancy] = useState<HospitalOccupancyDto[]>([]);
  const [loading, setLoading] = useState(false);
  // State cho bộ lọc ngày chung (cho card tổng quan và nội trú)
  const [dates, setDates] = useState<[Dayjs, Dayjs]>([dayjs().subtract(6, 'day'), dayjs()]);
  const [tempDates, setTempDates] = useState<[Dayjs, Dayjs]>([dayjs().subtract(6, 'day'), dayjs()]);
  const [error, setError] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [hasPermission, setHasPermission] = useState<boolean>(true);
  const token = localStorage.getItem('token');
  const [visitStats, setVisitStats] = useState<VisitStatsDto[]>([]);
  const [visitStatsError, setVisitStatsError] = useState<string>('');
  const [visitDates, setVisitDates] = useState<[Dayjs, Dayjs]>([dayjs().subtract(6, 'day'), dayjs()]);
  const [visitFilterOpen, setVisitFilterOpen] = useState(false);
  const [financialData, setFinancialData] = useState<FinancialReportDto[]>([]);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [financialError, setFinancialError] = useState('');
  const [financialDates, setFinancialDates] = useState<[Dayjs, Dayjs]>([dayjs().subtract(6, 'day'), dayjs()]);
  const [financialFilterOpen, setFinancialFilterOpen] = useState(false);


  // State for cumulative service statistics
  const [serviceStats, setServiceStats] = useState<any[]>([]);
  const [serviceStatsLoading, setServiceStatsLoading] = useState<boolean>(false);
  const [serviceStatsError, setServiceStatsError] = useState<string>('');
  const [serviceFilterOpen, setServiceFilterOpen] = useState<boolean>(false);
  const [serviceDates, setServiceDates] = useState<[Dayjs, Dayjs]>([dayjs().subtract(7, 'days'), dayjs()]);
  const [tempServiceDates, setTempServiceDates] = useState<[Dayjs, Dayjs]>([dayjs().subtract(7, 'days'), dayjs()]);
  const [serviceChartData, setServiceChartData] = useState<{chartData: any[], serviceTypes: string[]}>({ chartData: [], serviceTypes: [] });
  const [visibleServices, setVisibleServices] = useState<{ [key: string]: boolean }>({});

  // State for paraclinical service statistics
  const [paraclinicalStats, setParaclinicalStats] = useState<ParaclinicalServiceStatsDto[]>([]);
  const [paraclinicalLoading, setParaclinicalLoading] = useState<boolean>(false);
  const [paraclinicalError, setParaclinicalError] = useState<string>('');
  const [paraclinicalFilterOpen, setParaclinicalFilterOpen] = useState<boolean>(false);
  const [paraclinicalDates, setParaclinicalDates] = useState<[Dayjs, Dayjs]>([dayjs().subtract(7, 'days'), dayjs()]);
  const [tempParaclinicalDates, setTempParaclinicalDates] = useState<[Dayjs, Dayjs]>([dayjs().subtract(7, 'days'), dayjs()]);
  const [paraclinicalChartData, setParaclinicalChartData] = useState<{chartData: any[], serviceTypes: string[]}>({ chartData: [], serviceTypes: [] });
  const [visibleParaclinicalServices, setVisibleParaclinicalServices] = useState<{ [key: string]: boolean }>({});
  
  const [departmentRevenue, setDepartmentRevenue] = useState<DepartmentRevenueDto[]>([]);

  // State for real-time update
  const [isRealtime, setIsRealtime] = useState<boolean>(false);
  const [refreshInterval, setRefreshInterval] = useState<number>(60); // Default 60 seconds
  const [tempRefreshInterval, setTempRefreshInterval] = useState<number>(60);
  const [isIntervalModalVisible, setIsIntervalModalVisible] = useState<boolean>(false);

  const screens = useBreakpoint();
  const isMobile = !screens.lg;


  // Check authentication status when component mounts
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      if (!token) {
        // If no token, redirect to login
        navigate('/login', { replace: true });
        return false;
      }
      setIsAuthenticated(true);
      return true;
    };

    // Initial check
    checkAuth();

    // Listen for storage changes (in case of logout from another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' && !e.newValue) {
        navigate('/login', { replace: true });
      } else if (e.key === 'permissions' || e.key === 'token') {
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [navigate]);

  // Transform service stats data for chart
  const transformServiceStatsData = useCallback((data: any[]) => {
    // Group data by serviceType
    const groupedData: { [key: string]: any[] } = {};
    
    data.forEach(item => {
      if (!groupedData[item.serviceType]) {
        groupedData[item.serviceType] = [];
      }
      groupedData[item.serviceType].push({
        ...item,
        date: item.reportDate,
        value: item.cumulativeCount
      });
    });

    // Parse and sort dates in chronological order
    const parseDate = (dateStr: string) => {
      const [day, month, year] = dateStr.split('/').map(Number);
      return new Date(2000 + year, month - 1, day); // Using 2000 + year to handle 2-digit years
    };

    // Get all unique dates and sort them chronologically
    const allDates = Array.from(new Set(data.map(item => item.reportDate)))
      .sort((a, b) => parseDate(a).getTime() - parseDate(b).getTime());
    
    // Create chart data with all dates for each service type
    const chartData = allDates.map(date => {
      const dateData: any = { date };
      
      Object.keys(groupedData).forEach(serviceType => {
        const serviceData = groupedData[serviceType].find((d: any) => d.reportDate === date);
        dateData[serviceType] = serviceData ? serviceData.cumulativeCount : null;
      });
      
      return dateData;
    });
    
    return {
      chartData,
      serviceTypes: Object.keys(groupedData)
    };
  }, []);

  // Các hàm fetch dữ liệu
  const fetchHospitalOccupancy = useCallback(async () => {
    if (!isAuthenticated) return;
    
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    setLoading(true);
    try {
      const data = await reportService.getHospitalOccupancy();
      setHospitalOccupancy(data);
      setError('');
    } catch (err: any) {
      console.error('Error fetching hospital occupancy:', err);
      setError('Không lấy được dữ liệu số lượng người nằm viện!');
      setHospitalOccupancy([]);
    }
  }, [isAuthenticated, navigate]);

  const fetchStats = useCallback(async () => {
    if (!isAuthenticated) return;
    
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    setLoading(true);
    try {
      const fromDate = dates[0].startOf('day').format('YYYY-MM-DD');
      const toDate = dates[1].endOf('day').format('YYYY-MM-DD');
      const data = await reportService.getStatsCards(fromDate, toDate);
      setStats(data);
      setError('');
    } catch (err: any) {
      setError('Không lấy được dữ liệu chỉ số!');
      setStats([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, dates, navigate]);

  const fetchVisitStats = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setVisitStatsError('');
    
    try {
      const fromDate = visitDates[0].format('YYYY-MM-DD');
      const toDate = visitDates[1].format('YYYY-MM-DD');
      
      // The 'token' parameter is no longer needed, the interceptor handles it.
      const data = await reportService.getVisitStats(fromDate, toDate);
      setVisitStats(data);
    } catch (err: any) {
      // The 401/403 error is handled by the interceptor.
      console.error('Error fetching visit stats:', err);
      setVisitStatsError('Không thể tải dữ liệu thống kê. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [visitDates, isAuthenticated]);

  const fetchFinancialData = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setFinancialLoading(true);
    setFinancialError('');
    
    try {
      const data = await reportService.getFinancialReport(
        financialDates[0].format('YYYY-MM-DD'),
        financialDates[1].format('YYYY-MM-DD')
      );
      setFinancialData(data);
    } catch (err: any) {
      console.error('Error fetching financial data:', err);
      setFinancialError('Không thể tải dữ liệu báo cáo tài chính. Vui lòng thử lại sau.');
    } finally {
      setFinancialLoading(false);
    }
  }, [financialDates, isAuthenticated]);

  const fetchServiceStats = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setServiceStatsLoading(true);
    setServiceStatsError('');
    
    try {
      const data = await reportService.getCumulativeServiceStats(
        serviceDates[0].format('YYYY-MM-DD'),
        serviceDates[1].format('YYYY-MM-DD')
      );
      setServiceStats(data);
      const transformed = transformServiceStatsData(data);
      setServiceChartData(transformed);

      const initialVisibility: { [key: string]: boolean } = {};
      transformed.serviceTypes.forEach(serviceType => {
        initialVisibility[serviceType] = true;
      });
      setVisibleServices(prev => ({ ...initialVisibility, ...prev }));

    } catch (err: any) {
      console.error('Error fetching service stats:', err);
      setServiceStatsError('Không thể tải dữ liệu thống kê dịch vụ. Vui lòng thử lại sau.');
    } finally {
      setServiceStatsLoading(false);
    }
  }, [serviceDates, isAuthenticated, transformServiceStatsData]);

  const fetchParaclinicalStats = useCallback(async () => {
    if (!isAuthenticated) return;

    setParaclinicalLoading(true);
    setParaclinicalError('');
    
    try {
      const fromDate = paraclinicalDates[0].format('YYYY-MM-DD');
      const toDate = paraclinicalDates[1].format('YYYY-MM-DD');
      
      const data = await reportService.getParaclinicalServiceStats(fromDate, toDate);
      
      const serviceTypes = Array.from(new Set(data.map(item => item.serviceType)));
      const initialVisibility: { [key: string]: boolean } = {};
      serviceTypes.forEach(serviceType => {
        initialVisibility[serviceType] = true;
      });
      setVisibleParaclinicalServices(prev => ({ ...initialVisibility, ...prev }));

      const dates = Array.from(new Set(data.map(item => item.reportDate))).sort();
      const chartData = dates.map(date => {
        const dateData: any = { date };
        data
          .filter(item => item.reportDate === date)
          .forEach(item => {
            dateData[item.serviceType] = item.completedCount;
          });
        return dateData;
      });
      
      setParaclinicalStats(data);
      setParaclinicalChartData({
        chartData,
        serviceTypes
      });
    } catch (error: any) {
      console.error('Error fetching paraclinical service stats:', error);
      setParaclinicalError('Đã xảy ra lỗi khi tải dữ liệu thống kê cận lâm sàng. Vui lòng thử lại sau.');
    } finally {
      setParaclinicalLoading(false);
    }
  }, [paraclinicalDates, isAuthenticated]);

  const fetchDepartmentRevenue = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    
    if (!dates || !dates[0] || !dates[1]) {
      return;
    }
    
    setLoading(true);
    try {
      const fromDate = dates[0].format('YYYY-MM-DD');
      const toDate = dates[1].format('YYYY-MM-DD');
      
      const data = await reportService.getDepartmentRevenue(fromDate, toDate);
      setDepartmentRevenue(data);
    } catch (error: any) {
      console.error('Error fetching department revenue:', error);
      setDepartmentRevenue([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, dates]);

  // Effect for initial data load and manual refresh
  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      fetchHospitalOccupancy();
      fetchDepartmentRevenue();
    }
  }, [isAuthenticated, dates, fetchStats, fetchHospitalOccupancy, fetchDepartmentRevenue]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchVisitStats();
    }
  }, [isAuthenticated, visitDates, fetchVisitStats]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchFinancialData();
    }
  }, [isAuthenticated, financialDates, fetchFinancialData]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchServiceStats();
    }
  }, [isAuthenticated, serviceDates, fetchServiceStats]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchParaclinicalStats();
    }
  }, [isAuthenticated, paraclinicalDates, fetchParaclinicalStats]);

  // Effect for real-time polling
  useEffect(() => {
    if (isRealtime && isAuthenticated) {
      const fetchAllData = () => {
        fetchStats();
        fetchHospitalOccupancy();
        fetchVisitStats();
        fetchFinancialData();
        fetchServiceStats();
        fetchParaclinicalStats();
        fetchDepartmentRevenue();
      };
      
      fetchAllData();
      const intervalId = setInterval(fetchAllData, refreshInterval * 1000);
      
      return () => clearInterval(intervalId);
    }
  }, [isRealtime, isAuthenticated, refreshInterval, fetchStats, fetchHospitalOccupancy, 
      fetchVisitStats, fetchFinancialData, fetchServiceStats, fetchParaclinicalStats, 
      fetchDepartmentRevenue]);

  // Kiểm tra quyền khi component được mount
  useEffect(() => {
    const checkPermission = () => {
      try {
        const permissions = localStorage.getItem('permissions');
        if (!permissions) {
          setHasPermission(false);
          return;
        }

        try {
          const parsedPermissions = JSON.parse(permissions);
          if (Array.isArray(parsedPermissions)) {
            const hasViewReports = parsedPermissions.some((p: string) => 
              p.toLowerCase() === 'viewreports' || 
              p.toLowerCase() === 'view_reports' ||
              p.toLowerCase() === 'view reports'
            );
            setHasPermission(hasViewReports);
          } else if (typeof parsedPermissions === 'string') {
            const perm = parsedPermissions.toLowerCase();
            setHasPermission(
              perm === 'viewreports' || 
              perm === 'view_reports' ||
              perm === 'view reports' ||
              perm.includes('viewreports') ||
              perm.includes('view_reports')
            );
          } else {
            console.error('Unexpected permissions format:', parsedPermissions);
            setHasPermission(false);
          }
        } catch (e) {
          const perm = permissions.toLowerCase();
          setHasPermission(
            perm === 'viewreports' || 
            perm === 'view_reports' ||
            perm === 'view reports' ||
            perm.includes('viewreports') ||
            perm.includes('view_reports')
          );
        }
      } catch (error) {
        console.error('Error checking permissions:', error);
        setHasPermission(false);
      }
    };

    checkPermission();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'permissions' || e.key === 'token') {
        console.log('Storage changed, rechecking permissions...');
        checkPermission();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Xử lý thay đổi ngày
  const handleDateRangeChange = (newDates: any) => {
    if (newDates && newDates[0] && newDates[1]) {
      setTempDates([newDates[0], newDates[1]]);
    }
  };

  const handleApplyFilter = () => {
    setDates(tempDates);
    setFilterOpen(false);
  };

  const handleVisitDateChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setVisitDates([dates[0], dates[1]]);
    }
    setVisitFilterOpen(false);
  };

  const handleFinancialDateChange = (dates: any) => {
    if (dates) {
      setFinancialDates([dates[0], dates[1]]);
    }
  };

  const handleApplyFinancialFilter = () => {
    setFinancialFilterOpen(false);
    fetchFinancialData();
  };

  const handleServiceDateChange = (dates: any) => {
    if (dates) {
      setServiceDates([dates[0], dates[1]]);
    }
  };

  const handleApplyServiceFilter = () => {
    setServiceFilterOpen(false);
    fetchServiceStats();
  };

  const handleParaclinicalDateChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setTempParaclinicalDates([dates[0], dates[1]]);
    }
  };

  const handleOpenParaclinicalFilter = () => {
    setTempParaclinicalDates([...paraclinicalDates]);
    setParaclinicalFilterOpen(true);
  };

  const handleCancelParaclinicalFilter = () => {
    setParaclinicalFilterOpen(false);
  };

  const handleApplyParaclinicalFilter = () => {
    setParaclinicalDates([...tempParaclinicalDates]);
    setParaclinicalFilterOpen(false);
  };

  const toggleServiceVisibility = (serviceType: string) => {
    setVisibleServices(prev => {
      const isCurrentlyVisible = prev[serviceType] !== false;
      return { ...prev, [serviceType]: !isCurrentlyVisible };
    });
  };

  const toggleParaclinicalServiceVisibility = (serviceType: string) => {
    setVisibleParaclinicalServices(prev => {
      const isCurrentlyVisible = prev[serviceType] !== false;
      return { ...prev, [serviceType]: !isCurrentlyVisible };
    });
  };

  const handleLegendClick = (data: any) => {
    const { dataKey } = data;
    if (dataKey) {
      toggleServiceVisibility(dataKey);
    }
  };

  const handleParaclinicalLegendClick = (data: any) => {
    const { dataKey } = data;
    if (dataKey) {
      toggleParaclinicalServiceVisibility(dataKey);
    }
  };

  const getParaclinicalYAxisDomain = (): [number, number | string] => {
    if (!paraclinicalChartData.chartData.length) return [0, 'auto'];
    let max = 0;
    paraclinicalChartData.chartData.forEach(dataPoint => {
      Object.entries(dataPoint).forEach(([key, value]) => {
        if (key !== 'date' && visibleParaclinicalServices[key] !== false && typeof value === 'number') {
          if (value > max) {
            max = value;
          }
        }
      });
    });
    if (max === 0) return [0, 100];
    return [0, Math.ceil(max * 1.1)];
  };

  // Kiểm tra nếu không có quyền
  if (!hasPermission) {
    return (
      <div style={{ background: '#23293a', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Card style={{ width: 500, textAlign: 'center' }}>
          <Title level={3} style={{ color: '#ff4d4f' }}>Truy cập bị từ chối</Title>
          <p>Bạn không có quyền truy cập vào trang báo cáo này.</p>
          <p>Vui lòng liên hệ quản trị viên để được cấp quyền.</p>
        </Card>
      </div>
    );
  }

  // Render các card thống kê
  return (
    <div style={{ padding: '16px', background: '#1a1f33', minHeight: '100vh' }}>
      {/* Card Tổng quan */}
      <Card
        style={{ marginBottom: 16, background: '#1e1e2d', border: '1px solid #2a3042' }}
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #2a3042' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', margin: 0, flexDirection: isMobile ? 'column' : 'row' }}>
            <div style={{ marginBottom: isMobile ? 16 : 0, color: '#40a9ff', fontWeight: 600, fontSize: 15 }}>
              THỐNG KÊ TỔNG QUAN
              <div style={{ color: '#bfc7d5', fontWeight: 400, fontSize: 13, marginTop: 2 }}>
                Từ {dates[0]?.format('DD/MM/YYYY')} đến {dates[1]?.format('DD/MM/YYYY')}
              </div>
            </div>
            <Space direction={isMobile ? 'vertical' : 'horizontal'} align={isMobile ? 'end' : 'center'} style={{ width: isMobile ? '100%' : 'auto'}}>
              <Space>
                {!isMobile && <Text style={{ color: '#bfc7d5' }}>Tải tự động:</Text>}
                <Switch checked={isRealtime} onChange={setIsRealtime} />
              </Space>
              <Space>
                <Button
                  type="text"
                  icon={<SettingOutlined style={{ fontSize: 20, color: '#40a9ff' }} />}
                  onClick={() => {
                    setTempRefreshInterval(refreshInterval);
                    setIsIntervalModalVisible(true);
                  }}
                  title="Cấu hình thời gian"
                />
                <Button
                  type="text"
                  icon={<FilterOutlined style={{ fontSize: 20, color: '#40a9ff' }} />}
                  onClick={() => setFilterOpen(true)}
                  title="Bộ lọc thởi gian"
                />
              </Space>
            </Space>
          </div>
        </div>
        
        {/* Nội dung card tổng quan */}
        <div style={{ padding: 16 }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
              <Spin size="large" />
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ color: '#ff4d4f' }}>{error}</p>
              <Button 
                type="primary" 
                onClick={fetchStats}
                loading={loading}
              >
                Thử lại
              </Button>
            </div>
          ) : (
            <Row gutter={[16, 16]}>
              {stats.map((stat, index) => (
                <Col xs={24} sm={12} md={8} lg={6} key={index}>
                  <Card style={{ background: '#23293a', border: '1px solid #2a3042' }}>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ color: '#bfc7d5', fontSize: 14 }}>{stat.title}</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: '#40a9ff' }}>{stat.value}</div>
                    </div>
                    {stat.details && stat.details.map((detail, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#bfc7d5' }}>
                        <span>{detail.label}</span>
                        <span style={{ color: '#fff' }}>{detail.value}</span>
                      </div>
                    ))}
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>

        {/* Modal chọn khoảng thởi gian */}
        <Modal
          title="Chọn khoảng thởi gian"
          open={filterOpen}
          onOk={handleApplyFilter}
          onCancel={() => setFilterOpen(false)}
          okText="Áp dụng"
          cancelText="Hủy"
          centered
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <span style={{ marginRight: 8 }}>Khoảng thởi gian:</span>
              <RangePicker
                value={tempDates}
                onChange={handleDateRangeChange}
                format="DD/MM/YYYY"
                style={{ width: 250 }}
              />
            </div>
          </Space>
        </Modal>
      </Card>

      {/* Hàng 1: 2 card nằm ngang */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* Card Thống kê lượt khám - 50% chiều rộng */}
        <Col xs={24} md={12}>
          <Card
            style={{ 
              background: '#1e1e2d', 
              border: '1px solid #2a3042',
              borderRadius: '8px',
              height: '100%'
            }}
            bodyStyle={{ padding: 0, height: '100%' }}
          >
            <div style={{ 
              padding: '16px 24px', 
              borderBottom: '1px solid #2a3042',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ color: '#40a9ff', fontWeight: 600, fontSize: 15 }}>
                THỐNG KÊ LƯỢT KHÁM
                <div style={{ color: '#bfc7d5', fontWeight: 400, fontSize: 13, marginTop: 2 }}>
                  Từ {visitDates[0]?.format('DD/MM/YYYY')} đến {visitDates[1]?.format('DD/MM/YYYY')}
                </div>
              </div>
              <Button
                type="text"
                icon={<FilterOutlined style={{ fontSize: 20, color: '#40a9ff' }} />}
                onClick={() => setVisitFilterOpen(true)}
                title="Bộ lọc thời gian"
              />
            </div>
            
            <div style={{ padding: '16px', height: 'calc(100% - 81px)' }}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Spin size="large" />
                </div>
              ) : visitStatsError ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <p style={{ color: '#ff4d4f' }}>{visitStatsError}</p>
                  <Button 
                    type="primary" 
                    onClick={fetchVisitStats}
                    loading={loading}
                  >
                    Thử lại
                  </Button>
                </div>
              ) : (
                <div style={{ width: '100%', height: '100%', minHeight: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={visitStats}
                      margin={{
                        top: 10,
                        right: 30,
                        left: 0,
                        bottom: 0,
                      }}
                    >
                      <defs>
                        <linearGradient id="colorVisit" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#40a9ff" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#40a9ff" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a3042" vertical={false} />
                      <XAxis 
                        dataKey="ngay"  
                        stroke="#bfc7d5"
                        tick={{ fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis 
                        stroke="#bfc7d5"
                        tick={{ fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip 
                        contentStyle={{
                          background: '#2a3042',
                          border: '1px solid #434343',
                          borderRadius: '4px',
                          color: '#fff',
                          padding: '8px 12px',
                          fontSize: '12px',
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                        }}
                        formatter={(value: number) => [`${value}`, 'Lượt khám']}
                        labelFormatter={(label) => `Ngày: ${label}`}
                        itemStyle={{ color: '#fff' }}
                        labelStyle={{ color: '#bfc7d5', marginBottom: '4px' }}
                      />
                      <Area 
                        type="monotone"
                        dataKey="tongLuotKham"
                        stroke="#40a9ff"
                        strokeWidth={5}
                        fillOpacity={1}
                        fill="url(#colorVisit)"
                        activeDot={{
                          r: 8,
                          stroke: '#fff',
                          strokeWidth: 2,
                          fill: '#40a9ff'
                        }}
                        dot={{
                          r: 4,
                          stroke: '#40a9ff',
                          strokeWidth: 2,
                          fill: '#fff',
                          fillOpacity: 1
                        }}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        connectNulls={true}
                        name="Tổng lượt khám"
                      >
                        <LabelList 
                          dataKey="tongLuotKham"
                          position="top"
                          fill="#fff"
                          style={{
                            fontSize: '12px',
                            fontWeight: 'bold',
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                          }}
                          formatter={(value: any) => typeof value === 'number' ? value.toLocaleString() : value}
                        />
                      </Area>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </Card>
        </Col>

        {/* Card Người bệnh nội trú - 50% chiều rộng */}
        <Col xs={24} md={12}>
          <Card
            className="hospital-table-card"
            style={{ 
              background: '#1e1e2d', 
              border: '1px solid #2a3042',
              height: '100%'
            }}
            bodyStyle={{ padding: 0, height: '100%' }}
          >
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #2a3042' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ color: '#40a9ff', fontWeight: 600, fontSize: 15 }}>
                  NGƯỜI BỆNH NỘI TRÚ
                  <div style={{ color: '#bfc7d5', fontWeight: 400, fontSize: 13, marginTop: 2 }}>
                    Từ {dates[0]?.format('DD/MM/YYYY')} đến {dates[1]?.format('DD/MM/YYYY')}
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{ padding: 16, height: 'calc(100% - 81px)' }}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                  <Spin size="large" />
                </div>
              ) : error ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <p style={{ color: '#ff4d4f' }}>Failed to edit, Expected 1 occurrence but found 2 for old_string in file: D:\Code\erp\frontend\src\components\ReportTab.tsx</p>
                  <Button 
                    type="primary" 
                    onClick={fetchHospitalOccupancy}
                    loading={loading}
                  >
                    Thử lại
                  </Button>
                </div>
              ) : (
                <div className="hospital-table-html-wrapper" style={{ height: '100%', overflow: 'auto' }}>
                  <table className="hospital-table-html">
                    <thead>
                      <tr>
                        <th>Khoa</th>
                        <th>Hiện có</th>
                        <th>Số giường</th>
                        <th>Công suất</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hospitalOccupancy.map((item, index) => {
                        let colorClass = 'progress-green';
                        if (item.congSuatPhanTram >= 100) colorClass = 'progress-red';
                        else if (item.congSuatPhanTram >= 80) colorClass = 'progress-orange';
                        else if (item.congSuatPhanTram >= 60) colorClass = 'progress-yellow';
                        
                        return (
                          <tr key={index} className={index === 0 ? 'first-row' : ''}>
                            <td className={index === 0 ? 'first-row' : ''}>{item.tenKhoa}</td>
                            <td className={index === 0 ? 'first-row' : ''}>{item.soNguoiNamVien}</td>
                            <td className={index === 0 ? 'first-row' : ''}>{item.soGiuongTongCong}</td>
                            <td>
                              <div className="progress-container">
                                <div 
                                  className={`progress-bar ${colorClass}`}
                                  style={{ width: `${Math.min(100, item.congSuatPhanTram)}%` }}
                                ></div>
                                <div className="progress-text">
                                  {Math.round(item.congSuatPhanTram)}%
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Card>
        </Col>


        
      </Row>



      {/* Hàng 2: Card Thống kê tiền thu */}
      <Row style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Card
            style={{ 
              background: '#1e1e2d', 
              border: '1px solid #2a3042',
              borderRadius: '8px',
              height: '100%'
            }}
            bodyStyle={{ padding: 0, height: '100%' }}
          >
            <div style={{ 
              padding: '16px 24px', 
              borderBottom: '1px solid #2a3042',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ color: '#40a9ff', fontWeight: 600, fontSize: 15 }}>
                THỐNG KÊ TIỀN THU
                <div style={{ color: '#bfc7d5', fontWeight: 400, fontSize: 13, marginTop: 2 }}>
                  Từ {financialDates[0]?.format('DD/MM/YYYY')} đến {financialDates[1]?.format('DD/MM/YYYY')}
                </div>
              </div>
              <Button
                type="text"
                icon={<FilterOutlined style={{ color: '#40a9ff' }} />}
                onClick={() => setFinancialFilterOpen(true)}
              />
            </div>
            
            <div style={{ padding: '16px 24px' }}>
              {/* Các chỉ số tài chính */}
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={6}>
                  <div style={{ 
                    background: 'rgba(52, 168, 90, 0.1)', 
                    padding: '12px 16px',
                    borderRadius: '8px',
                    borderLeft: '4px solid #34a85a',
                    height: '100%'
                  }}>
                    <div style={{ color: '#bfc7d5', fontSize: 14, marginBottom: 4 }}>Tổng tiền</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>
                      {financialData.reduce((sum, item) => sum + (item.thanhTienBV || 0), 0).toLocaleString()} đ
                    </div>
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <div style={{ 
                    background: 'rgba(52, 168, 90, 0.1)', 
                    padding: '12px 16px',
                    borderRadius: '8px',
                    borderLeft: '4px solid #34a85a',
                    height: '100%'
                  }}>
                    <div style={{ color: '#bfc7d5', fontSize: 14, marginBottom: 4 }}>BHYT</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>
                      {financialData.reduce((sum, item) => sum + (item.tongChiPhiBH || 0), 0).toLocaleString()} đ
                    </div>
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <div style={{ 
                    background: 'rgba(52, 168, 90, 0.1)', 
                    padding: '12px 16px',
                    borderRadius: '8px',
                    borderLeft: '4px solid #34a85a',
                    height: '100%'
                  }}>
                    <div style={{ color: '#bfc7d5', fontSize: 14, marginBottom: 4 }}>Dịch vụ</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>
                      {financialData.reduce((sum, item) => sum + (item.tienBN100 || 0), 0).toLocaleString()} đ
                    </div>
                  </div>
                </Col>

              </Row>
              
              {/* Biểu đồ */}
              <div style={{ height: 250 }}>
                {financialLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin size="large" />
                  </div>
                ) : financialError ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <p style={{ color: '#ff4d4f' }}>{financialError}</p>
                    <Button 
                      type="primary" 
                      onClick={fetchFinancialData}
                      loading={financialLoading}
                    >
                      Thử lại
                    </Button>
                  </div>
                ) : financialData.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <p>Không có dữ liệu báo cáo tài chính</p>
                    <Button 
                      type="primary" 
                      onClick={fetchFinancialData}
                      loading={financialLoading}
                    >
                      Tải lại
                    </Button>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={financialData}
                      margin={{
                        top: 20, right: 30, left: 20, bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#2a3042" />
                      <XAxis 
                        dataKey="ngay"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#8c8c8c', fontSize: 12 }}
                        tickMargin={10}
                      />
                      <YAxis 
                        yAxisId="left"
                        orientation="left"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#8c8c8c', fontSize: 12 }}
                        width={80}
                        tickFormatter={(value: any) => typeof value === 'number' ? `${(value / 1000000).toFixed(0)}M` : ''}
                      />
                      <Tooltip 
                        contentStyle={{
                          background: '#2a3042',
                          border: '1px solid #434343',
                          borderRadius: '4px',
                          color: '#fff',
                          padding: '8px 12px',
                          fontSize: '12px'
                        }}
                        formatter={(value: number, name: string) => {
                          const formattedValue = value.toLocaleString() + ' đ';
                          const label = 
                            name === 'tongChiPhiBH' ? 'BHYT' :
                            name === 'tienBN100' ? 'Dịch vụ' :
                            name === 'thanhTienBV' ? 'Tổng tiền' : name;
                          return [formattedValue, label];
                        }}
                        labelFormatter={(label) => `Ngày: ${label}`}
                        itemStyle={{ color: '#fff' }}
                        labelStyle={{ color: '#8c8c8c', marginBottom: '4px' }}
                      />
                      <Legend formatter={(value, entry) => {
                        const { color } = entry;
                        return <span style={{ color }}>{value}</span>;
                      }}/>
                      <Bar yAxisId="left" dataKey="tongChiPhiBH" name="BHYT" fill="#1890ff">
                        <LabelList 
                          dataKey="tongChiPhiBH"
                          position="top"
                          formatter={(value: any) => typeof value === 'number' ? `${(value / 1000000).toFixed(1)}M` : ''}
                          fill="#fff"
                          style={{ fontSize: 11, fontWeight: 'bold' }}
                        />
                      </Bar>
                      <Bar yAxisId="left" dataKey="tienBN100" name="Dịch vụ (Bao gồm BN Cùng CT)" fill="#fadb14">
                        <LabelList 
                          dataKey="tienBN100"
                          position="top"
                          formatter={(value: any) => typeof value === 'number' ? `${(value / 1000000).toFixed(1)}M` : ''}
                          fill="#fff"
                          style={{ fontSize: 11, fontWeight: 'bold' }}
                        />
                      </Bar>

                      <Line 
                        yAxisId="left"
                        type="monotone"
                        dataKey="thanhTienBV"
                        name="Tổng tiền"
                        stroke="#ff7300"
                        strokeWidth={2}
                        dot={{
                          r: 4,
                          fill: '#ff7300',
                          stroke: '#fff',
                          strokeWidth: 2,
                        }}
                        activeDot={{
                          r: 6,
                          fill: '#ff7300',
                          stroke: '#fff',
                          strokeWidth: 2,
                        }}
                      >
                        <LabelList 
                          dataKey="thanhTienBV"
                          position="top"
                          formatter={(value: any) => typeof value === 'number' ? `${(value / 1000000).toFixed(1)}M` : ''}
                          fill="#ff7300"
                          style={{ fontSize: 11, fontWeight: 'bold' }}
                        />
                      </Line>
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Modal chọn khoảng thởi gian cho biểu đồ lượt khám */}
      <Modal
        title="Chọn khoảng thởi gian"
        open={visitFilterOpen}
        onOk={() => handleVisitDateChange(visitDates)}
        onCancel={() => setVisitFilterOpen(false)}
        okText="Áp dụng"
        cancelText="Hủy"
        centered
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <span style={{ marginRight: 8 }}>Khoảng thởi gian:</span>
            <RangePicker
              value={visitDates}
              onChange={(dates) => handleVisitDateChange(dates)}
              format="DD/MM/YYYY"
              style={{ width: 250 }}
            />
          </div>
        </Space>
      </Modal>

      {/* Card Thống kê dịch vụ luỹ kế */}
      <Card
        style={{ 
          marginBottom: 24, 
          background: '#1e1e2d', 
          border: '1px solid #2a3042',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ 
          padding: '16px 24px', 
          borderBottom: '1px solid #2a3042',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ color: '#40a9ff', fontWeight: 600, fontSize: 15 }}>
            THỐNG KÊ DỊCH VỤ LUỸ KẾ
            <div style={{ color: '#bfc7d5', fontWeight: 400, fontSize: 13, marginTop: 2 }}>
              Từ {serviceDates[0]?.format('DD/MM/YYYY')} đến {serviceDates[1]?.format('DD/MM/YYYY')}
            </div>
          </div>
          <Button
            type="text"
            icon={<FilterOutlined style={{ color: '#40a9ff' }} />}
            onClick={() => setServiceFilterOpen(true)}
          />
        </div>
        
        <div style={{ padding: '16px 24px' }}>
          {serviceStatsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
              <Spin size="large" />
            </div>
          ) : serviceStatsError ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ color: '#ff4d4f', marginBottom: 16 }}>{serviceStatsError}</p>
              <Button 
                type="primary" 
                onClick={fetchServiceStats}
                loading={serviceStatsLoading}
              >
                Thử lại
              </Button>
            </div>
          ) : serviceStats.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p>Không có dữ liệu thống kê dịch vụ luỹ kế</p>
              <Button 
                type="primary" 
                onClick={fetchServiceStats}
                loading={serviceStatsLoading}
              >
                Tải lại
              </Button>
            </div>
          ) : (
            <div style={{ height: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={serviceChartData.chartData}
                  margin={{
                    top: 10,
                    right: 30,
                    left: 20,
                    bottom: 0,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#2a3042" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#8c8c8c' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    tick={{ fill: '#8c8c8c' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    contentStyle={{
                      background: '#2a3042',
                      border: '1px solid #434343',
                      borderRadius: '4px',
                      color: '#fff',
                      padding: '8px 12px',
                      fontSize: '12px'
                    }}
                    formatter={(value: any, name: any, props: any) => {
                      return [value, name];
                    }}
                    labelFormatter={(label) => `Ngày: ${label}`}
                    itemStyle={{ color: '#fff' }}
                    labelStyle={{ color: '#8c8c8c', marginBottom: '4px' }}
                  />
                  <Legend 
                    verticalAlign="top"
                    height={36}
                    onClick={handleLegendClick}
                    formatter={(value) => {
                      const isVisible = visibleServices[value] !== false;
                      return (
                        <span style={{
                          color: isVisible ? '#bfc7d5' : '#666',
                          cursor: 'pointer',
                          textDecoration: isVisible ? 'none' : 'line-through',
                          fontSize: 12
                        }}>
                          {value}
                        </span>
                      );
                    }}
                  />
                  {serviceChartData.serviceTypes
                    .map((serviceType, index) => {
                      const colors = [
                        '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', 
                        '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#d4380d'
                      ];
                      const color = colors[index % colors.length];
                      const isVisible = visibleServices[serviceType] !== false;
                      
                      return (
                        <Line
                          key={serviceType}
                          type="monotone"
                          dataKey={serviceType}
                          name={serviceType}
                          stroke={color}
                          strokeWidth={3}
                          dot={false}
                          activeDot={{
                            r: 6,
                            fill: color,
                            stroke: '#fff',
                            strokeWidth: 2,
                          }}
                          connectNulls
                          hide={!isVisible}
                        />
                      );
                    })}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </Card>

      {/* Modal chọn khoảng thời gian cho thống kê dịch vụ luỹ kế */}
      <Modal
        title="Chọn khoảng thời gian"
        open={serviceFilterOpen}
        onOk={handleApplyServiceFilter}
        onCancel={() => setServiceFilterOpen(false)}
        okText="Áp dụng"
        cancelText="Hủy"
        centered
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <span style={{ marginRight: 8 }}>Từ ngày - Đến ngày:</span>
            <RangePicker
              value={serviceDates}
              onChange={handleServiceDateChange}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
            />
          </div>
        </Space>
      </Modal>

      {/* Card Thống kê cận lâm sàng */}
      <Card
        style={{ 
          marginBottom: 24, 
          background: '#1e1e2d', 
          border: '1px solid #2a3042',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ 
          padding: '16px 24px', 
          borderBottom: '1px solid #2a3042',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ color: '#40a9ff', fontWeight: 600, fontSize: 15 }}>
            THỐNG KÊ CẬN LÂM SÀNG
            <div style={{ color: '#bfc7d5', fontWeight: 400, fontSize: 13, marginTop: 2 }}>
              Từ {paraclinicalDates[0]?.format('DD/MM/YYYY')} đến {paraclinicalDates[1]?.format('DD/MM/YYYY')}
            </div>
          </div>
          <Button
            type="text"
            icon={<FilterOutlined style={{ color: '#40a9ff' }} />}
            onClick={handleOpenParaclinicalFilter}
          />
        </div>
        
        <div style={{ padding: '16px 24px' }}>
          {paraclinicalLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
              <Spin size="large" />
            </div>
          ) : paraclinicalError ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ color: '#ff4d4f', marginBottom: 16 }}>{paraclinicalError}</p>
              <Button 
                type="primary" 
                onClick={fetchParaclinicalStats}
                loading={paraclinicalLoading}
              >
                Thử lại
              </Button>
            </div>
          ) : paraclinicalStats.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p>Không có dữ liệu thống kê cận lâm sàng</p>
              <Button 
                type="primary" 
                onClick={fetchParaclinicalStats}
                loading={paraclinicalLoading}
              >
                Tải lại
              </Button>
            </div>
          ) : (
            <div style={{ height: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={paraclinicalChartData.chartData}
                  margin={{
                    top: 10,
                    right: 30,
                    left: 20,
                    bottom: 0,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#2a3042" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#8c8c8c' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    tick={{ fill: '#8c8c8c' }}
                    tickLine={false}
                    axisLine={false}
                    domain={getParaclinicalYAxisDomain()}
                  />
                  <Tooltip 
                    contentStyle={{
                      background: '#2a3042',
                      border: '1px solid #434343',
                      borderRadius: '4px',
                      color: '#fff',
                      padding: '8px 12px',
                      fontSize: '12px'
                    }}
                    formatter={(value: any, name: any) => {
                      // Format the value with thousand separators
                      return [Number(value).toLocaleString(), name];
                    }}
                    labelFormatter={(label) => `Ngày: ${label}`}
                    itemStyle={{ color: '#fff' }}
                    labelStyle={{ color: '#8c8c8c', marginBottom: '4px' }}
                  />
                  <Legend 
                    verticalAlign="top"
                    height={36}
                    onClick={handleParaclinicalLegendClick}
                    formatter={(value) => {
                      const isVisible = visibleParaclinicalServices[value] !== false;
                      return (
                        <span style={{
                          color: isVisible ? '#bfc7d5' : '#666',
                          cursor: 'pointer',
                          textDecoration: isVisible ? 'none' : 'line-through',
                          fontSize: 12
                        }}>
                          {value}
                        </span>
                      );
                    }}
                  />
                  {paraclinicalChartData.serviceTypes
                    .map((serviceType, index) => {
                      const colors = [
                        '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', 
                        '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#d4380d'
                      ];
                      const color = colors[index % colors.length];
                      const isVisible = visibleParaclinicalServices[serviceType] !== false;

                      return (
                        <Line
                          key={serviceType}
                          type="monotone"
                          dataKey={serviceType}
                          name={serviceType}
                          stroke={color}
                          strokeWidth={3}
                          dot={false}
                          activeDot={{
                            r: 6,
                            fill: color,
                            stroke: '#fff',
                            strokeWidth: 2,
                          }}
                          connectNulls
                          hide={!isVisible}
                        />
                      );
                    })}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </Card>

      {/* Modal chọn khoảng thời gian cho thống kê cận lâm sàng */}
      <Modal
        title="Chọn khoảng thời gian"
        open={paraclinicalFilterOpen}
        onOk={handleApplyParaclinicalFilter}
        onCancel={handleCancelParaclinicalFilter}
        okText="Áp dụng"
        cancelText="Hủy"
        centered
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <span style={{ marginRight: 8 }}>Từ ngày - Đến ngày:</span>
            <RangePicker
              value={tempParaclinicalDates}
              onChange={handleParaclinicalDateChange}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
            />
          </div>
        </Space>
      </Modal>

      {/* Modal chọn khoảng thời gian cho báo cáo tài chính */}
      <Modal
        title="Chọn khoảng thời gian"
        open={financialFilterOpen}
        onOk={handleApplyFinancialFilter}
        onCancel={() => setFinancialFilterOpen(false)}
        okText="Áp dụng"
        cancelText="Hủy"
        centered
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <span style={{ marginRight: 8 }}>Từ ngày - Đến ngày:</span>
            <RangePicker
              value={financialDates}
              onChange={handleFinancialDateChange}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
            />
          </div>
        </Space>
      </Modal>

      {/* Modal for setting refresh interval */}
      <Modal
        title="Cấu hình thời gian tải lại"
        open={isIntervalModalVisible}
        onOk={() => {
          setRefreshInterval(tempRefreshInterval);
          setIsIntervalModalVisible(false);
        }}
        onCancel={() => setIsIntervalModalVisible(false)}
        okText="Lưu"
        cancelText="Hủy"
        centered
      >
        <Space>
          <Text>Thời gian làm mới (giây):</Text>
          <InputNumber
            min={10}
            value={tempRefreshInterval}
            onChange={(value) => setTempRefreshInterval(value || 60)}
          />
        </Space>
      </Modal>

      {/* Card biểu đồ cơ cấu tiền thu */}
      <Card
        style={{
          background: '#23293a',
          color: '#fff',
          borderRadius: 12,
          marginTop: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          border: 'none',
          margin: '0 0 24px 0',
          padding: 0
        }}
        bodyStyle={{ padding: 0 }}
      >
        <div style={{
          background: '#22304a',
          color: '#40a9ff',
          padding: '10px 16px 4px 16px',
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          fontWeight: 600,
          fontSize: 15,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            CƠ CẤU TIỀN THU
            <div style={{ color: '#bfc7d5', fontWeight: 400, fontSize: 13, marginTop: 2 }}>
              Từ {dates[0]?.format('DD/MM/YYYY')} đến {dates[1]?.format('DD/MM/YYYY')}
            </div>
          </div>
          <Button
            type="text"
            icon={<FilterOutlined style={{ fontSize: 20, color: '#40a9ff' }} />}
            style={{ marginRight: 0, marginTop: 0 }}
            onClick={() => setFilterOpen(true)}
            title="Bộ lọc thởi gian"
          />
        </div>
        <div style={{ background: '#23293a', padding: 16, borderRadius: '0 0 12px 12px', minHeight: 400 }}>
          {/* Debug info */}
          
          <div style={{ display: 'flex', gap: 24, flexDirection: isMobile ? 'column' : 'row' }}>
                        {/* Pie Chart */}
            <div style={{ flex: 1 }}>

              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={departmentRevenue.length > 0 ? departmentRevenue.map(item => {
                      const phanTramStr = item.phanTram || '0';
                      const value = parseFloat(phanTramStr.replace('%', '').replace(',', '.'));
                      return {
                        name: item.tenKhoa,
                        value: isNaN(value) ? 0 : value
                      };
                    }).filter(item => item.value > 0) : []}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    innerRadius={40}
                    label={({ percent }) => `${((percent || 0) * 100).toFixed(1)}%`}
                    labelLine={false}
                  >
                    {departmentRevenue.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={[
                        '#4e79a7', '#f28e2c', '#e15759', '#76b7b2', '#59a14f', 
                        '#edc949', '#af7aa1', '#ff9da7', '#9c755f', '#bab0ab',
                        '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
                      ][index % 15]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              
              {/* Legend below the chart */}
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                justifyContent: 'center', 
                gap: '12px', 
                marginTop: '16px',
                padding: '0 16px'
              }}>
                {departmentRevenue.map((item, index) => {
                  const phanTramStr = item.phanTram || '0';
                  const value = parseFloat(phanTramStr.replace('%', '').replace(',', '.'));
                  if (isNaN(value) || value <= 0) return null;
                  
                  return (
                    <div key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: [
                          '#4e79a7', '#f28e2c', '#e15759', '#76b7b2', '#59a14f', 
                          '#edc949', '#af7aa1', '#ff9da7', '#9c755f', '#bab0ab',
                          '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
                        ][index % 15],
                        marginRight: 6
                      }} />
                      <span style={{ 
                        color: '#bfc7d5', 
                        fontSize: 11,
                        whiteSpace: 'nowrap'
                      }}>
                        {item.tenKhoa}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Chi tiết doanh thu */}
            <div style={{ flex: 1, paddingLeft: isMobile ? 0 : 16, paddingTop: isMobile ? 24 : 0 }}>
              <div style={{ color: '#bfc7d5', fontSize: 13, marginBottom: 12, fontWeight: 600 }}>
                ĐƠN VỊ: TRIỆU ĐỒNG
              </div>
              {departmentRevenue.length === 0 ? (
                <div style={{ color: '#bfc7d5', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>
                  {loading ? 'Đang tải dữ liệu...' : 'Không có dữ liệu'}
                </div>
              ) : (
                departmentRevenue.map((item, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: 8,
                    padding: '4px 0'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: [
                          '#4e79a7', '#f28e2c', '#e15759', '#76b7b2', '#59a14f', 
                          '#edc949', '#af7aa1', '#ff9da7', '#9c755f', '#bab0ab',
                          '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
                        ][index % 15],
                        marginRight: 8
                      }} />
                      <span style={{ color: '#fff', fontSize: 13 }}>{item.tenKhoa}</span>
                    </div>
                    <span style={{ color: '#40a9ff', fontSize: 13, fontWeight: 600 }}>{item.tongDoanhThu}</span>
                  </div>
                ))
              )}
              <div style={{ 
                borderTop: '1px solid #2c3a54', 
                marginTop: 12, 
                paddingTop: 12 
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  marginBottom: 4 
                }}>
                  <span style={{ color: '#bfc7d5', fontSize: 13 }}>Tổng tiền thu:</span>
                  <span style={{ color: '#40a9ff', fontSize: 13, fontWeight: 600 }}>
                    {departmentRevenue.length > 0 ? departmentRevenue.reduce((sum, item) => sum + item.doanhThuGoc, 0).toLocaleString() : '0'}
                  </span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  marginBottom: 4 
                }}>
                  <span style={{ color: '#52c41a', fontSize: 13 }}>BHYT:</span>
                  <span style={{ color: '#52c41a', fontSize: 13, fontWeight: 600 }}>
                    {departmentRevenue.length > 0 ? (departmentRevenue.reduce((sum, item) => sum + item.doanhThuGoc, 0) * 0.7).toLocaleString() : '0'} (70%)
                  </span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between' 
                }}>
                  <span style={{ color: '#fa541c', fontSize: 13 }}>Bệnh nhân chi trả:</span>
                  <span style={{ color: '#fa541c', fontSize: 13, fontWeight: 600 }}>
                    {departmentRevenue.length > 0 ? (departmentRevenue.reduce((sum, item) => sum + item.doanhThuGoc, 0) * 0.3).toLocaleString() : '0'} (30%)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ReportTab;
